<template>
  <div class="erp-recepcio-page">
    <card :title="$t('tools.erp_integration.recepcio')" :hide-title="true" id="erp-recepcio">
      <div class="recepcio-header">
        <h1 class="recepcio-header__title">{{ $t("tools.erp_integration.recepcio") }}</h1>
        <div class="recepcio-header__actions">
          <router-link
            to="/tools/erp-integration"
            class="btn btn-md btn-secondary recepcio-header__icon-button"
            :aria-label="$t('common.back_to_list')"
            :title="$t('common.back_to_list')"
          >
            <span class="dx-icon dx-icon-arrowleft" aria-hidden="true"></span>
          </router-link>
          <button
            type="button"
            class="btn btn-md btn-secondary recepcio-header__icon-button"
            :disabled="loading"
            :aria-label="$t('common.refresh')"
            :title="$t('common.refresh')"
            @click="bootstrap"
          >
            <span class="dx-icon dx-icon-refresh" aria-hidden="true"></span>
          </button>
        </div>
      </div>

      <div class="view-switcher" role="tablist">
        <button
          type="button"
          class="view-switcher__btn"
          :class="{ active: activeView === 'setup' }"
          role="tab"
          :aria-selected="activeView === 'setup'"
          @click="activeView = 'setup'"
        >
          {{ $t("tools.erp_integration.recepcio_view_setup") }}
        </button>
        <button
          type="button"
          class="view-switcher__btn"
          :class="{ active: activeView === 'mappings' }"
          role="tab"
          :aria-selected="activeView === 'mappings'"
          @click="activeView = 'mappings'"
        >
          {{ $t("tools.erp_integration.recepcio_view_mappings") }}
        </button>
        <button
          type="button"
          class="view-switcher__btn"
          :class="{ active: activeView === 'queue' }"
          role="tab"
          :aria-selected="activeView === 'queue'"
          @click="activeView = 'queue'"
        >
          {{ $t("tools.erp_integration.recepcio_view_queue") }}
        </button>
        <button
          type="button"
          class="view-switcher__btn"
          :class="{ active: activeView === 'audit' }"
          role="tab"
          :aria-selected="activeView === 'audit'"
          @click="activeView = 'audit'"
        >
          {{ $t("tools.erp_integration.recepcio_view_audit") }}
        </button>
        <button
          type="button"
          class="view-switcher__btn"
          :class="{ active: activeView === 'preview' }"
          role="tab"
          :aria-selected="activeView === 'preview'"
          @click="activeView = 'preview'"
        >
          {{ $t("tools.erp_integration.recepcio_view_preview") }}
        </button>
      </div>

      <div v-if="activeView === 'setup'" class="view-panel">
        <div class="checkbox-row checkbox-row--master">
          <label>
            <input v-model="settingsForm.reception_integration_enabled" type="checkbox" />
            {{ $t("tools.erp_integration.reception_integration_enabled") }}
          </label>
          <small>{{ $t("tools.erp_integration.reception_integration_enabled_hint") }}</small>
        </div>

        <fieldset class="settings-grid" :class="{ 'settings-grid--disabled': !settingsForm.reception_integration_enabled }" :disabled="!settingsForm.reception_integration_enabled">
          <div class="form-group">
            <label>{{ $t("tools.erp_integration.reception_endpoint_path") }}</label>
            <input v-model="settingsForm.wm_endpoint_path" type="text" class="form-control" />
          </div>

          <div class="form-group">
            <label>{{ $t("tools.erp_integration.reception_default_tip_document") }}</label>
            <select v-model="settingsForm.default_tip_document" class="form-control">
              <option v-for="item in tipDocumentOptions" :key="item" :value="item">{{ item }}</option>
            </select>
          </div>

          <div class="form-group">
            <label>{{ $t("tools.erp_integration.reception_retry_max_attempts") }}</label>
            <input v-model.number="settingsForm.retry_max_attempts" type="number" min="0" max="100" class="form-control" />
          </div>

          <div class="form-group">
            <label>{{ $t("tools.erp_integration.reception_retry_backoff") }}</label>
            <input v-model.number="settingsForm.retry_backoff_base_seconds" type="number" min="10" max="3600" class="form-control" />
          </div>

          <div class="form-group">
            <label>{{ $t("tools.erp_integration.reception_batch_size") }}</label>
            <input v-model.number="settingsForm.batch_size" type="number" min="1" max="1000" class="form-control" />
          </div>

          <div class="form-group">
            <label>{{ $t("tools.erp_integration.reception_timeout_seconds") }}</label>
            <input v-model.number="settingsForm.timeout_seconds" type="number" min="30" max="600" class="form-control" />
          </div>

        </fieldset>

        <div class="settings-actions">
          <button type="button" class="btn btn-primary" :disabled="savingSettings" @click="saveSettings">
            {{ $t("common.save") }}
          </button>
        </div>
      </div>

      <div v-if="activeView === 'mappings'" class="view-panel">
        <div class="panel-toolbar">
          <button type="button" class="btn btn-sm btn-outline-primary" @click="openMappingPopup()">
            {{ $t("common.add") }}
          </button>
          <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="loadingMappings" @click="loadMappings">
            {{ $t("common.refresh") }}
          </button>
        </div>

        <DxDataGrid
          :data-source="mappings"
          key-expr="id"
          :show-borders="true"
          :row-alternation-enabled="true"
          :column-auto-width="true"
          :no-data-text="$t('common.no_data_text')"
        >
          <DxColumn data-field="mapping_type" :caption="$t('tools.erp_integration.reception_mapping_type')" />
          <DxColumn data-field="source_kind" :caption="$t('tools.erp_integration.reception_source_kind')" />
          <DxColumn data-field="internal_key" :caption="$t('tools.erp_integration.reception_internal_key')" />
          <DxColumn data-field="internal_label" :caption="$t('tools.erp_integration.reception_internal_label')" />
          <DxColumn data-field="wm_key" :caption="$t('tools.erp_integration.reception_wm_key')" />
          <DxColumn data-field="static_value" :caption="$t('tools.erp_integration.reception_static_value')" />
          <DxColumn data-field="priority" :caption="$t('tools.erp_integration.reception_priority')" width="90" />
          <DxColumn data-field="is_active" :caption="$t('tools.erp_integration.reception_active')" width="90" />
          <DxColumn type="buttons" width="110">
            <DxButton icon="edit" :hint="$t('common.edit')" :on-click="onEditMapping" />
            <DxButton icon="trash" :hint="$t('common.delete')" :on-click="onDeleteMapping" />
          </DxColumn>
        </DxDataGrid>

        <DxPopup
          :visible.sync="mappingPopupVisible"
          :title="editingMappingId ? $t('common.edit') : $t('common.add')"
          :show-close-button="true"
          :width="700"
          :height="540"
        >
          <div class="mapping-form">
            <div class="form-group">
              <label>{{ $t("tools.erp_integration.reception_mapping_type") }}</label>
              <select v-model="mappingForm.mapping_type" class="form-control">
                <option v-for="item in mappingTypeOptions" :key="item" :value="item">{{ item }}</option>
              </select>
            </div>
            <div class="form-group">
              <label>{{ $t("tools.erp_integration.reception_source_kind") }}</label>
              <select v-model="mappingForm.source_kind" class="form-control">
                <option value="INTERNAL_UUID">INTERNAL_UUID</option>
                <option value="STATIC_VALUE">STATIC_VALUE</option>
                <option value="ERP_ENTITY_RECORD">ERP_ENTITY_RECORD</option>
              </select>
            </div>
            <div class="form-group">
              <label>{{ $t("tools.erp_integration.reception_internal_key") }}</label>
              <input v-model="mappingForm.internal_key" type="text" class="form-control" />
            </div>
            <div class="form-group">
              <label>{{ $t("tools.erp_integration.reception_internal_label") }}</label>
              <input v-model="mappingForm.internal_label" type="text" class="form-control" />
            </div>
            <div class="form-group">
              <label>{{ $t("tools.erp_integration.reception_wm_key") }}</label>
              <input v-model="mappingForm.wm_key" type="text" class="form-control" />
            </div>
            <div class="form-group">
              <label>{{ $t("tools.erp_integration.reception_static_value") }}</label>
              <input v-model="mappingForm.static_value" type="text" class="form-control" />
            </div>
            <div class="form-group">
              <label>{{ $t("tools.erp_integration.reception_priority") }}</label>
              <input v-model.number="mappingForm.priority" type="number" min="0" max="9999" class="form-control" />
            </div>
            <div class="checkbox-row">
              <label><input v-model="mappingForm.is_active" type="checkbox" /> {{ $t("tools.erp_integration.reception_active") }}</label>
              <label><input v-model="mappingForm.is_fallback" type="checkbox" /> {{ $t("tools.erp_integration.reception_fallback") }}</label>
            </div>
            <div class="settings-actions">
              <button type="button" class="btn btn-primary" :disabled="savingMapping" @click="saveMapping">
                {{ $t("common.save") }}
              </button>
            </div>
          </div>
        </DxPopup>
      </div>

      <div v-if="activeView === 'queue'" class="view-panel">
        <div class="queue-filters">
          <input
            v-model="queueFilters.search"
            type="text"
            class="form-control"
            :placeholder="$t('tools.erp_integration.reception_search_placeholder')"
          />
          <select v-model="queueFilters.status" class="form-control">
            <option value="">{{ $t("common.all") }}</option>
            <option v-for="item in queueStatusOptions" :key="item" :value="item">{{ item }}</option>
          </select>
          <select v-model="queueFilters.source_type" class="form-control">
            <option value="">{{ $t("common.all") }}</option>
            <option value="SM_RECEPTION">SM_RECEPTION</option>
            <option value="SCALE_RECEPTION">SCALE_RECEPTION</option>
          </select>
          <button type="button" class="btn btn-sm btn-outline-primary" :disabled="loadingQueue" @click="loadQueue">
            {{ $t("common.search") }}
          </button>
          <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="loadingQueue" @click="runBulkRetry">
            {{ $t("tools.erp_integration.reception_bulk_retry") }}
          </button>
        </div>

        <DxDataGrid
          :data-source="queue"
          key-expr="uuid"
          :show-borders="true"
          :row-alternation-enabled="true"
          :column-auto-width="true"
          :no-data-text="$t('common.no_data_text')"
          @selection-changed="onQueueSelectionChanged"
        >
          <DxSelection mode="single" />
          <DxColumn data-field="updated_at" :caption="$t('common.last_update')" data-type="datetime" :sort-order="'desc'" />
          <DxColumn data-field="created_at" :caption="$t('common.created_at')" data-type="datetime" />
          <DxColumn data-field="status" :caption="$t('common.status')" />
          <DxColumn data-field="source_type" :caption="$t('tools.erp_integration.reception_source_type')" />
          <DxColumn data-field="internal_document_number" :caption="$t('tools.erp_integration.reception_internal_document')" />
          <DxColumn data-field="shipping_note_number" :caption="$t('tools.erp_integration.reception_shipping_note')" />
          <DxColumn data-field="partner_name" :caption="$t('common.partner')" />
          <DxColumn data-field="product_name" :caption="$t('common.product')" />
          <DxColumn data-field="warehouse_name" :caption="$t('tools.erp_integration.reception_entity_warehouse')" />
          <DxColumn data-field="quantity" :caption="$t('common.quantity')" />
          <DxColumn data-field="attempt_count" :caption="$t('tools.erp_integration.reception_attempts')" width="90" />
          <DxColumn data-field="wm_document_number" :caption="$t('tools.erp_integration.reception_wm_document')" />
          <DxColumn data-field="last_error_code" :caption="$t('tools.erp_integration.reception_last_error')" cell-template="errorCell" />
          <template #errorCell="{ data }">
            <span :title="data.data.last_error_message || data.data.last_error_code" :class="{ 'text-danger': !!data.data.last_error_code }">
              {{ data.data.last_error_code || '-' }}
            </span>
          </template>
          <DxColumn type="buttons" width="220">
            <DxButton icon="find" :hint="$t('tools.erp_integration.recepcio_view_audit')" :on-click="onOpenAudit" />
            <DxButton icon="preview" :hint="$t('tools.erp_integration.reception_preview_run')" :on-click="onPreviewQueueItem" />
            <DxButton icon="check" :hint="$t('tools.erp_integration.consum_validate')" :on-click="onValidateQueueItem" />
            <DxButton icon="export" :hint="$t('tools.erp_integration.consum_send')" :on-click="onSendQueueItem" />
            <DxButton icon="refresh" :hint="$t('tools.erp_integration.consum_retry')" :on-click="onRetryQueueItem" />
            <DxButton icon="close" :hint="$t('tools.erp_integration.consum_cancel')" :on-click="onCancelQueueItem" />
          </DxColumn>
        </DxDataGrid>
      </div>

      <div v-if="activeView === 'audit'" class="view-panel">
        <div v-if="!selectedQueueItem" class="placeholder-panel">
          <h5>{{ $t("tools.erp_integration.reception_audit_empty_title") }}</h5>
          <p>{{ $t("tools.erp_integration.reception_audit_empty_description") }}</p>
        </div>

        <div v-else class="audit-wrap">
          <div class="audit-head">
            <div><strong>{{ $t("tools.erp_integration.reception_source_type") }}:</strong> {{ selectedQueueItem.source_type }}</div>
            <div><strong>{{ $t("tools.erp_integration.reception_internal_document") }}:</strong> {{ selectedQueueItem.internal_document_number || selectedQueueItem.source_document_number || '-' }}</div>
            <div><strong>{{ $t("tools.erp_integration.reception_shipping_note") }}:</strong> {{ selectedQueueItem.shipping_note_number || '-' }}</div>
            <div><strong>{{ $t("common.partner") }}:</strong> {{ selectedQueueItem.partner_name || '-' }}</div>
            <div><strong>{{ $t("common.product") }}:</strong> {{ selectedQueueItem.product_name || '-' }}</div>
            <div><strong>{{ $t("common.last_update") }}:</strong> {{ selectedQueueItem.updated_at || selectedQueueItem.created_at }}</div>
            <div><strong>{{ $t("common.status") }}:</strong> {{ selectedQueueItem.status }}</div>
          </div>

          <DxDataGrid
            :data-source="selectedQueueItem.events || []"
            key-expr="id"
            :show-borders="true"
            :row-alternation-enabled="true"
            :column-auto-width="true"
            :word-wrap-enabled="true"
            :no-data-text="$t('common.no_data_text')"
          >
            <DxColumn data-field="created_at" :caption="$t('common.created_at')" data-type="datetime" width="160" />
            <DxColumn data-field="event_type" :caption="$t('tools.erp_integration.reception_event_type')" width="140" />
            <DxColumn data-field="previous_status" :caption="$t('tools.erp_integration.reception_previous_status')" width="120" />
            <DxColumn data-field="new_status" :caption="$t('tools.erp_integration.reception_new_status')" width="120" />
            <DxColumn data-field="message" :caption="$t('common.message')" cell-template="auditMessageCell" />
            <template #auditMessageCell="{ data }">
              <div class="audit-message-cell">
                {{ data.data.message }}
              </div>
            </template>
          </DxDataGrid>
        </div>
      </div>

      <div v-if="activeView === 'preview'" class="view-panel">
        <div class="panel-toolbar">
          <button
            type="button"
            class="btn btn-sm btn-outline-primary"
            :disabled="previewLoading || !selectedQueueItem"
            @click="runPreview()"
          >
            {{ $t("tools.erp_integration.reception_preview_run") }}
          </button>
        </div>

        <div v-if="!previewResult" class="placeholder-panel">
          <h5>{{ $t("tools.erp_integration.reception_preview_empty_title") }}</h5>
          <p>{{ $t("tools.erp_integration.reception_preview_empty_description") }}</p>
        </div>

        <div v-else class="preview-wrap">
          <div class="preview-head">
            <div><strong>{{ $t("tools.erp_integration.reception_source_type") }}:</strong> {{ previewContext?.source_type || '-' }}</div>
            <div><strong>{{ $t("tools.erp_integration.reception_source_document") }}:</strong> {{ previewContext?.source_document_number || '-' }}</div>
            <div
              class="preview-badge"
              :class="previewResult.validation?.valid ? 'preview-badge--ok' : 'preview-badge--bad'"
            >
              {{ previewResult.validation?.valid
                ? $t("tools.erp_integration.reception_preview_validation_valid")
                : $t("tools.erp_integration.reception_preview_validation_invalid") }}
            </div>
            <div v-if="previewIsStale" class="preview-badge preview-badge--stale">
              {{ $t("tools.erp_integration.reception_preview_stale") }}
            </div>
          </div>

          <div class="preview-section">
            <h5>{{ $t("tools.erp_integration.reception_preview_issues_title") }} ({{ previewIssues.length }})</h5>
            <div v-if="!previewIssues.length" class="preview-ok">
              {{ $t("tools.erp_integration.reception_preview_no_issues") }}
            </div>
            <div v-else class="preview-table-wrap">
              <table class="preview-table">
                <thead>
                  <tr>
                    <th>{{ $t("tools.erp_integration.reception_preview_issue_code") }}</th>
                    <th>{{ $t("tools.erp_integration.reception_preview_issue_field") }}</th>
                    <th>{{ $t("tools.erp_integration.reception_preview_issue_message") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(issue, idx) in previewIssues" :key="'issue-' + idx">
                    <td><code>{{ issue.code || '-' }}</code></td>
                    <td>{{ issue.field || '-' }}</td>
                    <td>{{ issue.message || '-' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="preview-section">
            <h5>{{ $t("tools.erp_integration.reception_preview_mappings_title") }}</h5>

            <div v-if="missingMappings.length" class="preview-warning">
              <strong>{{ $t("tools.erp_integration.reception_preview_mapping_issues_title") }}:</strong>
              <ul>
                <li v-for="(mapping, idx) in missingMappings" :key="'missing-' + idx">
                  {{ mappingIssueLabel(mapping) }}
                </li>
              </ul>
              <small>{{ $t("tools.erp_integration.reception_preview_mapping_issue_hint") }}</small>
            </div>

            <div v-if="mappingResolutions.length" class="preview-table-wrap">
              <table class="preview-table">
                <thead>
                  <tr>
                    <th>{{ $t("tools.erp_integration.reception_preview_mapping_type") }}</th>
                    <th>{{ $t("tools.erp_integration.reception_preview_mapping_entity") }}</th>
                    <th>{{ $t("tools.erp_integration.reception_preview_mapping_internal") }}</th>
                    <th>{{ $t("tools.erp_integration.reception_preview_mapping_value") }}</th>
                    <th>{{ $t("tools.erp_integration.reception_preview_mapping_status") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(mapping, idx) in mappingResolutions"
                    :key="'resolution-' + idx"
                    :class="{ 'preview-row--warning': !mapping.matched, 'preview-row--clickable': !mapping.matched }"
                    :tabindex="mapping.matched ? null : 0"
                    @click="gotoMissingMapping(mapping)"
                    @keydown.enter="gotoMissingMapping(mapping)"
                  >
                    <td>{{ mapping.mapping_type || '-' }}</td>
                    <td>{{ mappingEntityLabel(mapping) }}</td>
                    <td>{{ mapping.internal_key || '-' }}</td>
                    <td>{{ mapping.value || '-' }}</td>
                    <td>
                      <span class="preview-status" :class="mappingStatusClass(mapping)">
                        {{ $t(`tools.erp_integration.${mappingStatusKey(mapping)}`) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="preview-ok">
              {{ $t("tools.erp_integration.reception_preview_mappings_none") }}
            </div>
          </div>

          <div class="preview-section">
            <div class="preview-section-header">
              <h5>{{ $t("tools.erp_integration.reception_preview_payload_title") }}</h5>
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                @click="copyPayload"
              >
                {{ $t("tools.erp_integration.reception_preview_copy_payload") }}
              </button>
            </div>
            <pre class="preview-payload">{{ formattedPayload || '{}' }}</pre>
          </div>
        </div>
      </div>
    </card>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import notify from "devextreme/ui/notify";
import DxDataGrid, { DxColumn, DxSelection, DxButton } from "devextreme-vue/data-grid";
import { DxPopup } from "devextreme-vue/popup";

function createDefaultMappingForm() {
  return {
    mapping_type: "SUPPLIER",
    source_kind: "INTERNAL_UUID",
    internal_key: "",
    internal_label: "",
    wm_key: "",
    static_value: "",
    priority: 100,
    is_active: true,
    is_fallback: false,
  };
}

export default {
  name: "ErpRecepcioPage",
  components: {
    DxDataGrid,
    DxColumn,
    DxSelection,
    DxButton,
    DxPopup,
  },
  data() {
    return {
      activeView: "setup",
      loading: false,
      loadingQueue: false,
      loadingMappings: false,
      savingSettings: false,
      savingMapping: false,
      previewLoading: false,
      previewContext: null,
      previewIsStale: false,
      mappingPopupVisible: false,
      editingMappingId: null,
      mappingForm: createDefaultMappingForm(),
      settingsForm: {
        reception_integration_enabled: false,
        wm_endpoint_path: "/datasnap/rest/TServerMethods/IntrariFurnizori",
        default_tip_document: "AVIZ INTRARE",
        retry_max_attempts: 7,
        retry_backoff_base_seconds: 60,
        batch_size: 100,
        timeout_seconds: 90,
      },
      queueFilters: {
        search: "",
        status: "",
        source_type: "",
        limit: 50,
      },
      tipDocumentOptions: [
        "FACTURA INTRARE",
        "AVIZ INTRARE",
        "FACTURA LA AVIZ",
        "INVOICE",
        "BON FISCAL",
        "BON ACHIZITIE",
      ],
      queueStatusOptions: [
        "NEW",
        "VALIDATED",
        "MAPPED",
        "SENT",
        "CONFIRMED",
        "ERROR",
        "RETRY_WAITING",
        "DEAD",
        "CANCELED",
      ],
      mappingTypeOptions: ["SUPPLIER", "PRODUCT", "WAREHOUSE", "SUBUNITATE", "MEASURE_UNIT"],
    };
  },
  computed: {
    ...mapGetters({
      queue: "erpIntegration/receptionQueue",
      mappings: "erpIntegration/receptionMappings",
      receptionSettings: "erpIntegration/receptionSettings",
      selectedQueueItem: "erpIntegration/receptionSelectedItem",
      previewResult: "erpIntegration/receptionPreview",
    }),
    previewIssues() {
      return this.previewResult?.validation?.issues || [];
    },
    mappingResolutions() {
      return this.previewResult?.mapping_resolutions || [];
    },
    missingMappings() {
      return this.previewResult?.mapping_issues || [];
    },
    formattedPayload() {
      const payload = this.previewResult?.payload;
      return payload && typeof payload === "object" ? JSON.stringify(payload, null, 2) : "";
    },
  },
  mounted() {
    this.bootstrap();
  },
  methods: {
    ...mapActions({
      fetchReceptionQueue: "erpIntegration/fetchReceptionQueue",
      fetchReceptionQueueItem: "erpIntegration/fetchReceptionQueueItem",
      validateReceptionQueueItem: "erpIntegration/validateReceptionQueueItem",
      sendReceptionQueueItem: "erpIntegration/sendReceptionQueueItem",
      retryReceptionQueueItem: "erpIntegration/retryReceptionQueueItem",
      cancelReceptionQueueItem: "erpIntegration/cancelReceptionQueueItem",
      bulkRetryReceptionQueue: "erpIntegration/bulkRetryReceptionQueue",
      fetchReceptionMappings: "erpIntegration/fetchReceptionMappings",
      storeReceptionMapping: "erpIntegration/storeReceptionMapping",
      updateReceptionMapping: "erpIntegration/updateReceptionMapping",
      destroyReceptionMapping: "erpIntegration/destroyReceptionMapping",
      fetchReceptionSettings: "erpIntegration/fetchReceptionSettings",
      updateReceptionSettings: "erpIntegration/updateReceptionSettings",
      previewReceptionQueueItem: "erpIntegration/previewReceptionQueueItem",
    }),
    async bootstrap() {
      this.loading = true;
      try {
        await Promise.all([this.loadSettings(), this.loadMappings(), this.loadQueue()]);
      } finally {
        this.loading = false;
      }
    },
    async loadSettings() {
      const settings = await this.fetchReceptionSettings();
      this.settingsForm = {
        ...this.settingsForm,
        ...settings,
      };
    },
    async saveSettings() {
      this.savingSettings = true;
      try {
        const settingsPayload = { ...this.settingsForm };
        delete settingsPayload.enable_auto_send;
        delete settingsPayload.enable_scale_source;
        delete settingsPayload.enable_sm_source;
        const result = await this.updateReceptionSettings(settingsPayload);
        this.settingsForm = {
          ...this.settingsForm,
          ...result,
        };
        notify(this.$t("tools.erp_integration.reception_settings_saved"), "success", 2000);
      } catch (e) {
        this.showActionError(e, "reception_action_error");
      } finally {
        this.savingSettings = false;
      }
    },
    async loadMappings() {
      this.loadingMappings = true;
      try {
        await this.fetchReceptionMappings({ limit: 200 });
      } finally {
        this.loadingMappings = false;
      }
    },
    openMappingPopup(item = null) {
      if (!item) {
        this.editingMappingId = null;
        this.mappingForm = createDefaultMappingForm();
      } else {
        this.editingMappingId = item.id;
        this.mappingForm = {
          mapping_type: item.mapping_type || "SUPPLIER",
          source_kind: item.source_kind || "INTERNAL_UUID",
          internal_key: item.internal_key || "",
          internal_label: item.internal_label || "",
          wm_key: item.wm_key || "",
          static_value: item.static_value || "",
          priority: item.priority ?? 100,
          is_active: !!item.is_active,
          is_fallback: !!item.is_fallback,
        };
      }

      this.mappingPopupVisible = true;
    },
    onEditMapping(event) {
      this.openMappingPopup(event.row?.data || null);
    },
    async onDeleteMapping(event) {
      const item = event.row?.data;
      if (!item?.id) {
        return;
      }

      if (!window.confirm(this.$t("tools.erp_integration.reception_confirm_delete_mapping"))) {
        return;
      }

      try {
        await this.destroyReceptionMapping(item.id);
        notify(this.$t("tools.erp_integration.reception_mapping_deleted"), "success", 2000);
        await this.loadMappings();
      } catch (e) {
        this.showActionError(e, "reception_action_error");
      }
    },
    async saveMapping() {
      this.savingMapping = true;
      try {
        if (this.editingMappingId) {
          await this.updateReceptionMapping({
            id: this.editingMappingId,
            payload: this.mappingForm,
          });
        } else {
          await this.storeReceptionMapping(this.mappingForm);
        }

        this.mappingPopupVisible = false;
        notify(this.$t("tools.erp_integration.reception_mapping_saved"), "success", 2000);
        await this.loadMappings();
      } catch (e) {
        this.showActionError(e, "reception_action_error");
      } finally {
        this.savingMapping = false;
      }
    },
    async loadQueue() {
      this.loadingQueue = true;
      try {
        await this.fetchReceptionQueue(this.queueFilters);
      } finally {
        this.loadingQueue = false;
      }
    },
    async runBulkRetry() {
      try {
        await this.bulkRetryReceptionQueue({
          status_list: ["ERROR", "DEAD"],
          limit: 100,
        });
        notify(this.$t("tools.erp_integration.reception_bulk_retry_done"), "success", 2000);
        await this.loadQueue();
      } catch (e) {
        this.showActionError(e, "reception_action_error");
      }
    },
    async onValidateQueueItem(event) {
      const item = event.row?.data;
      if (!item?.uuid) {
        return;
      }

      try {
        await this.validateReceptionQueueItem({ uuid: item.uuid, strictMode: true });
        await this.refreshQueueItem(item.uuid);
        this.markPreviewStaleIfCurrent(item.uuid);
        notify(this.$t("tools.erp_integration.reception_validate_done"), "success", 2000);
      } catch (e) {
        this.showActionError(e, "reception_action_error");
      }
    },
    async onSendQueueItem(event) {
      const item = event.row?.data;
      if (!item?.uuid) {
        return;
      }

      try {
        await this.sendReceptionQueueItem({ uuid: item.uuid, force: false });
        await this.refreshQueueItem(item.uuid);
        this.markPreviewStaleIfCurrent(item.uuid);
        notify(this.$t("tools.erp_integration.reception_send_done"), "success", 2000);
      } catch (e) {
        this.showActionError(e, "reception_action_error");
      }
    },
    async onRetryQueueItem(event) {
      const item = event.row?.data;
      if (!item?.uuid) {
        return;
      }

      try {
        await this.retryReceptionQueueItem({ uuid: item.uuid, mode: "SCHEDULE" });
        await this.refreshQueueItem(item.uuid);
        this.markPreviewStaleIfCurrent(item.uuid);
        notify(this.$t("tools.erp_integration.reception_retry_done"), "success", 2000);
      } catch (e) {
        this.showActionError(e, "reception_action_error");
      }
    },
    async onCancelQueueItem(event) {
      const item = event.row?.data;
      if (!item?.uuid) {
        return;
      }

      const reason = window.prompt(
        this.$t("tools.erp_integration.reception_cancel_reason_prompt"),
        this.$t("tools.erp_integration.reception_cancel_reason_default")
      );
      if (!reason) {
        return;
      }

      try {
        await this.cancelReceptionQueueItem({ uuid: item.uuid, reason });
        await this.refreshQueueItem(item.uuid);
        this.markPreviewStaleIfCurrent(item.uuid);
        notify(this.$t("tools.erp_integration.reception_cancel_done"), "success", 2000);
      } catch (e) {
        this.showActionError(e, "reception_action_error");
      }
    },
    async onOpenAudit(event) {
      const item = event.row?.data;
      if (!item?.uuid) {
        return;
      }

      try {
        await this.fetchReceptionQueueItem(item.uuid);
        this.activeView = "audit";
      } catch (e) {
        this.showActionError(e, "reception_action_error");
      }
    },
    onPreviewQueueItem(event) {
      this.runPreview(event.row?.data || null);
    },
    async onQueueSelectionChanged(event) {
      const selected = event.selectedRowsData?.[0] || null;
      if (!selected?.uuid) {
        return;
      }

      if (this.previewContext?.uuid && selected.uuid !== this.previewContext.uuid) {
        this.previewIsStale = true;
      }

      await this.fetchReceptionQueueItem(selected.uuid);
    },
    markPreviewStaleIfCurrent(uuid) {
      if (uuid && uuid === this.previewContext?.uuid) {
        this.previewIsStale = true;
      }
    },
    async refreshQueueItem(uuid) {
      await Promise.all([this.loadQueue(), this.fetchReceptionQueueItem(uuid)]);
    },
    async runPreview(item = null) {
      const target = item || this.selectedQueueItem || this.previewContext;
      if (!target?.uuid) {
        notify(this.$t("tools.erp_integration.reception_preview_no_item"), "warning", 2500);
        return;
      }

      if (!target?.source_uuid || !target?.source_type) {
        notify(this.$t("tools.erp_integration.reception_preview_source_missing"), "warning", 2500);
        return;
      }

      this.previewLoading = true;
      try {
        const result = await this.previewReceptionQueueItem({
          source_type: target.source_type,
          source_uuid: target.source_uuid,
          wm_tip_document: target.wm_tip_document || this.settingsForm.default_tip_document,
        });
        this.previewContext = target;
        this.previewIsStale = false;
        this.activeView = "preview";
        notify(
          result?.validation?.valid
            ? this.$t("tools.erp_integration.reception_preview_done")
            : this.$t("tools.erp_integration.reception_preview_invalid"),
          result?.validation?.valid ? "success" : "warning",
          2500
        );
      } catch (e) {
        this.showActionError(e, "reception_preview_error");
      } finally {
        this.previewLoading = false;
      }
    },
    async copyPayload() {
      const text = this.formattedPayload || "{}";
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
        } else {
          const textarea = document.createElement("textarea");
          textarea.value = text;
          textarea.setAttribute("readonly", "");
          textarea.style.position = "fixed";
          textarea.style.opacity = "0";
          document.body.appendChild(textarea);
          textarea.select();
          const copied = document.execCommand("copy");
          document.body.removeChild(textarea);
          if (!copied) {
            throw new Error("execCommand copy failed");
          }
        }
        notify(this.$t("tools.erp_integration.reception_preview_copy_done"), "success", 2000);
      } catch (e) {
        notify(this.$t("tools.erp_integration.reception_preview_copy_failed"), "error", 3000);
      }
    },
    gotoMissingMapping(mapping) {
      if (mapping?.matched) {
        return;
      }

      this.activeView = "mappings";
      notify(this.$t("tools.erp_integration.reception_preview_mapping_goto"), "info", 2500);
    },
    extractErrorMessage(error) {
      // errors.error holds the real underlying reason (e.g. per-entity nomenclator mapping detail);
      // the top-level `message` is often a generic wrapper string, so only fall back to it when no detail exists
      const detail = this.extractBackendErrorDetail(error?.response?.data);

      return (
        detail ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        ""
      );
    },
    extractBackendErrorDetail(data) {
      const errorBag = data?.errors?.error;
      const entries = Array.isArray(errorBag) ? errorBag : errorBag ? [errorBag] : [];

      return entries
        .flat()
        .filter(Boolean)
        .join(" | ");
    },
    showActionError(error, fallbackKey) {
      const message = this.extractErrorMessage(error);
      notify(message || this.$t(`tools.erp_integration.${fallbackKey}`), "error", 3000);
    },
    mappingStatusKey(mapping) {
      if (mapping?.matched) {
        return "reception_preview_mapping_status_matched";
      }

      if (mapping?.fallback) {
        return "reception_preview_mapping_status_fallback";
      }

      return "reception_preview_mapping_status_missing";
    },
    mappingStatusClass(mapping) {
      if (mapping?.matched) {
        return "preview-status--ok";
      }

      if (mapping?.fallback) {
        return "preview-status--warn";
      }

      return "preview-status--bad";
    },
    mappingEntityLabel(mapping) {
      const mappingType = String(mapping?.mapping_type || "").toLowerCase();
      const translationKey = `tools.erp_integration.reception_entity_${mappingType}`;

      return mappingType && this.$te(translationKey) ? this.$t(translationKey) : (mapping?.entity_label || "-");
    },
    mappingIssueLabel(mapping) {
      const entity = this.mappingEntityLabel(mapping) || mapping?.mapping_type || "entity";
      const label = mapping?.internal_label || mapping?.internal_key || "-";
      return `${entity} (${mapping?.mapping_type || ""}): ${label}`;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/components/smagriaTabs.scss";

.recepcio-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.recepcio-header__title {
  margin: 0;
  color: #323232;
  font-size: 32px;
  font-weight: 700;
  line-height: 1.2;
}

.recepcio-header__actions {
  display: flex;
  flex: 0 0 auto;
  gap: 8px;
}

.recepcio-header__icon-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
}

.view-switcher {
  @include smagria-tab-list;
  flex-wrap: wrap;
}

.view-switcher__btn {
  @include smagria-tab-button;
}

.view-panel {
  border: 1px solid #dfe6ef;
  border-radius: 10px;
  background: #fff;
  padding: 14px;
}

.settings-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(240px, 1fr));
  gap: 12px;
  border: 0;
  margin: 0;
  padding: 0;
}

.settings-grid--disabled {
  opacity: 0.55;
}

.checkbox-row {
  grid-column: 1 / -1;
  display: flex;
  gap: 18px;
  align-items: center;
  flex-wrap: wrap;
}

.checkbox-row--master {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 12px;
  margin-bottom: 14px;
  border: 1px solid #d8dee6;
  border-radius: 6px;
  background: #f7f9fb;
}

.settings-actions {
  grid-column: 1 / -1;
  display: flex;
  justify-content: flex-end;
}

.panel-toolbar {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: 10px;
}

.mapping-form {
  display: grid;
  grid-template-columns: repeat(2, minmax(220px, 1fr));
  gap: 10px;
}

.queue-filters {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr auto auto;
  gap: 8px;
  margin-bottom: 10px;
}

.placeholder-panel {
  border: 1px solid #dfe6ef;
  border-radius: 12px;
  background: #ffffff;
  padding: 16px;
}

.audit-wrap {
  display: grid;
  gap: 10px;
}

.audit-head {
  display: grid;
  grid-template-columns: repeat(3, minmax(180px, 1fr));
  gap: 8px;
}

.preview-wrap {
  display: grid;
  gap: 12px;
}

.preview-head {
  display: grid;
  grid-template-columns: repeat(3, minmax(180px, 1fr));
  gap: 8px;
  align-items: center;
}

.preview-badge {
  padding: 6px 10px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 13px;
  text-align: center;
}

.preview-badge--ok {
  background: #e7f7ee;
  color: #1e7e34;
  border: 1px solid #bfe5cc;
}

.preview-badge--bad {
  background: #fdecec;
  color: #c0392b;
  border: 1px solid #f5c6c6;
}

.preview-badge--stale {
  background: #fdf3ec;
  color: #a8510f;
  border: 1px solid #f0c9a8;
}

.preview-section {
  border: 1px solid #dfe6ef;
  border-radius: 8px;
  padding: 10px 12px;
}

.preview-section h5 {
  margin: 0 0 8px;
  font-size: 14px;
}

.preview-ok {
  background: #f2f9f4;
  border: 1px solid #cfe8d8;
  color: #1e7e34;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 13px;
}

.preview-warning {
  background: #fdf3ec;
  border: 1px solid #f0c9a8;
  color: #a8510f;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 13px;
  margin-bottom: 10px;
}

.preview-warning ul {
  margin: 6px 0;
  padding-left: 18px;
}

.preview-table-wrap {
  overflow-x: auto;
}

.preview-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.preview-table th,
.preview-table td {
  border: 1px solid #dfe6ef;
  padding: 6px 8px;
  text-align: left;
  vertical-align: top;
}

.preview-table th {
  background: #f4f7fb;
  font-weight: 600;
}

.preview-row--warning td {
  background: #fdf3ec;
}

.preview-row--clickable {
  cursor: pointer;
}

.preview-row--clickable:hover td {
  background: #fae8d8;
}

.preview-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.preview-section-header h5 {
  margin: 0;
}

.preview-status {
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  display: inline-block;
}

.preview-status--ok {
  background: #e7f7ee;
  color: #1e7e34;
}

.preview-status--warn {
  background: #fdf3ec;
  color: #a8510f;
}

.preview-status--bad {
  background: #fdecec;
  color: #c0392b;
}

.preview-payload {
  background: #f8fafc;
  border: 1px solid #dfe6ef;
  border-radius: 6px;
  padding: 10px;
  max-height: 420px;
  overflow: auto;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.audit-message-cell {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.4;
  padding: 4px 0;
}

@media (max-width: 992px) {
  .settings-grid,
  .mapping-form,
  .queue-filters,
  .audit-head,
  .preview-head {
    grid-template-columns: 1fr;
  }
}
</style>
